import React, { useRef } from 'react';
import { PanResponder, View } from 'react-native';
import { DNASwipeLengths, DNASwipeSpeeds, } from './DNAGestureDetector.types';
/** TODO: Align with product & design on best defaults */
const defaults = {
    defaultDirections: new Set(['right', 'down']),
    defaultLengths: new Set(['short']),
    defaultSpeeds: new Set(['fast']),
    defaultMovementTolerance: 50,
    defaultEdgeDistanceTolerance: 50,
};
/**
 * This component can be used to enable gesture detection on any wrapped components
 * and is constrained to the boundaries of the child elements. Currently, the supported
 * gestures are as follows:
 *  - Swipe
 *  - Edge Swipe
 *
 * See the following simple example implementation for reference:
 * @example
 * const edgeGestureTrigger:DNAEdgeSwipeGestureTrigger = {
 *   gestureType:'DNAEdgeSwipeGesture',
 *   activeEdges: new Set(['top', 'right']),
 *   activeDirections: new Set(['down']),
 *   activeSwipeLengths: new Set(['short']),
 *   onRelease: ({edges,directions}) => {
 *     edges.has('left') && directions.has('right') && someFunctionCall()
 *   }
 * }
 * <DNAGestureDetector gestureTriggers={[edgeGestureTrigger]}>
 *  <DNABox style={{ width:500, height:500 }}>
 *    <DNAText>Some Text</DNAText>
 *  </DNABox>
 * </DNAGestureDetector>
 */
const DNAGestureDetector = ({ children, gestureTriggers, disabled, fill }) => {
    const viewLayout = useRef();
    const gestureStartTime = useRef(0);
    const panResponder = useRef(PanResponder.create({
        onPanResponderStart: () => {
            gestureStartTime.current = Date.now();
        },
        // Ask to be the responder:
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        /** Avoid termination when text selection is made */
        onPanResponderTerminationRequest: () => false,
        onPanResponderRelease: (evt, gestureState) => {
            /** The user has released all touches while this view is the
             * responder. This typically means a gesture has succeeded */
            identifyAndHandleTriggeredGestures(gestureState);
        },
    }));
    /** If no gestures are defined or the disabled prop is enabled, just return the children */
    if (!gestureTriggers?.length || disabled)
        return <>{children}</>;
    /** The assumption here is that the gesture is done and we are doing analysis to identify which
     * gesture triggers should be fired. We then call the trigger's onRelease, passing in the
     * appropriate gesture release object type */
    const identifyAndHandleTriggeredGestures = ({ x0, y0, dx, dy }) => {
        const vDistanceSign = Math.sign(dy);
        const hDistanceSign = Math.sign(dx);
        const vDirection = vDistanceSign === 0
            ? undefined
            : vDistanceSign > -1
                ? 'down'
                : 'up';
        const hDirection = hDistanceSign === 0
            ? undefined
            : hDistanceSign > -1
                ? 'right'
                : 'left';
        const gestureDirections = [
            ...(vDirection ? [vDirection] : []),
            ...(hDirection ? [hDirection] : []),
        ];
        /** TODO: The left and top props are available at runtime but not in the interface.
         * Should we patch the LayoutRectangle interface to include these props?  */
        const { left = 0, width = 0, top = 0, height = 0 } = { ...viewLayout.current };
        const initialLeftEdgeDistance = Math.abs(x0 - left);
        const initialRightEdgeDistance = Math.abs((left + width) - x0);
        const initialTopEdgeDistance = Math.abs(y0 - top);
        const initialBottomEdgeDistance = Math.abs((top + height) - y0);
        /** NOTE: Apple trackpad inertial scrolling can make the duration seem unusually long */
        const gestureDuration = Date.now() - gestureStartTime.current;
        /** Utility function which compares the direction of the gesture with the enabled directions in the trigger */
        const isActiveDirection = (activeDirections) => gestureDirections.some(direction => activeDirections?.has(direction));
        /** Utility funcion to detect if the length of the gesture meets the minimum length defined in the trigger  */
        const isActiveSwipeLength = (activeDirections = defaults.defaultDirections, activeSwipeLengths = defaults.defaultLengths) => {
            const activeSwipeLengthValues = Array.from(activeSwipeLengths).map(swipeLength => DNASwipeLengths[swipeLength]);
            const minSwipeLength = Math.min(...activeSwipeLengthValues);
            const horizontalDirections = new Set(['left', 'right']);
            const verticallDirections = new Set(['up', 'down']);
            const isActiveHorizontalSwipeLength = Array.from(horizontalDirections).some(direction => activeDirections.has(direction)) &&
                Math.abs(dx) > minSwipeLength;
            const isActiveVerticalSwipeLength = Array.from(verticallDirections).some(direction => activeDirections.has(direction)) &&
                Math.abs(dy) > minSwipeLength;
            return isActiveHorizontalSwipeLength || isActiveVerticalSwipeLength;
        };
        const isWithinSwipeSpeedTolerance = (activeSwipeSpeeds = defaults.defaultSpeeds) => {
            const activeSwipeSpeedValues = Array.from(activeSwipeSpeeds).map(swipeSpeed => DNASwipeSpeeds[swipeSpeed]);
            const maxSwipeSpeedDuration = Math.max(...activeSwipeSpeedValues);
            /** TODO: currently we are assuming that anything shorter than the slowest active swipe action will return as a positive match, thus,
             * a trigger configured as a long gesture will also accept short duration gestures. Conversely, triggers configured as short duration
             * triggers will only fire on short duration gestures. Adjusting the logic below can enable distinct detection of short and long duration
             * gestures for triggers configured for long durations if it is deemed a necessary feature */
            return gestureDuration < maxSwipeSpeedDuration;
        };
        /** Utility function to ensure that the gesture meets the minimum movement distance threshold */
        const isWithinSwipeDistanceTolerance = (movementDistanceTolerance = defaults.defaultMovementTolerance) => {
            return Math.abs(dx) > movementDistanceTolerance ||
                Math.abs(dy) > movementDistanceTolerance;
        };
        const activeEdgeChecks = {
            left: (edgeDistanceTolerance) => initialLeftEdgeDistance < edgeDistanceTolerance,
            right: (edgeDistanceTolerance) => initialRightEdgeDistance < edgeDistanceTolerance,
            top: (edgeDistanceTolerance) => initialTopEdgeDistance < edgeDistanceTolerance,
            bottom: (edgeDistanceTolerance) => initialBottomEdgeDistance < edgeDistanceTolerance,
        };
        /** Utility function to detect gestures initiated near configured trigger edges */
        const isActiveEdge = (activeEdge, edgeDistanceTolerance = defaults.defaultEdgeDistanceTolerance) => {
            if (!activeEdge)
                return true;
            return Array.from(activeEdge).some(edge => activeEdgeChecks[edge](edgeDistanceTolerance));
        };
        /** First we filter out the gestures which do not meet the trigger criteria for each gesture type */
        const triggeredGestures = gestureTriggers
            .filter(gesture => {
            switch (gesture.gestureType) {
                case 'DNASwipeGesture': {
                    const { activeDirections, activeSwipeLengths, activeSwipeSpeeds, movementDistanceTolerance = defaults.defaultMovementTolerance, } = gesture;
                    return isActiveDirection(activeDirections) &&
                        isActiveSwipeLength(activeDirections, activeSwipeLengths) &&
                        isWithinSwipeDistanceTolerance(movementDistanceTolerance) &&
                        isWithinSwipeSpeedTolerance(activeSwipeSpeeds);
                }
                case 'DNAEdgeSwipeGesture': {
                    const { activeDirections, activeSwipeLengths, activeSwipeSpeeds, movementDistanceTolerance, edgeDistanceTolerance, activeEdges, } = gesture;
                    return isActiveDirection(activeDirections) &&
                        isActiveSwipeLength(activeDirections, activeSwipeLengths) &&
                        isWithinSwipeDistanceTolerance(movementDistanceTolerance) &&
                        isWithinSwipeSpeedTolerance(activeSwipeSpeeds) &&
                        isActiveEdge(activeEdges, edgeDistanceTolerance);
                }
                /** TODO: buildout this gesture */
                // case 'DNAPanGesture': {
                //   const { activeDirections, movementDistanceTolerance } = gesture
                //   console.log('Checking DNAPanGesture', {})
                //   break
                // }
                default:
                    break;
            }
        });
        /** Then, we generate the appropriate gesture release event based on the gesture type and pass it to the onRelease function */
        triggeredGestures.forEach((gesture) => {
            const { gestureType, onRelease } = gesture;
            if (!viewLayout.current)
                return;
            switch (gestureType) {
                case 'DNASwipeGesture': {
                    const newGestureReleaseEvent = {
                        directions: new Set(gestureDirections),
                        elementLayout: viewLayout.current,
                    };
                    onRelease?.(newGestureReleaseEvent);
                    break;
                }
                case 'DNAEdgeSwipeGesture': {
                    const { activeEdges, edgeDistanceTolerance = defaults.defaultEdgeDistanceTolerance } = gesture;
                    const triggeredActiveEdges = Array.from(activeEdges ?? []).filter(edge => {
                        return activeEdgeChecks[edge](edgeDistanceTolerance);
                    });
                    const newGestureReleaseEvent = {
                        directions: new Set(gestureDirections),
                        edges: new Set(triggeredActiveEdges),
                        elementLayout: viewLayout.current,
                    };
                    onRelease?.(newGestureReleaseEvent);
                    break;
                }
                case 'DNAPanGesture': {
                    const newGestureReleaseEvent = {
                        elementLayout: viewLayout.current,
                    };
                    onRelease?.(newGestureReleaseEvent);
                    break;
                }
            }
        });
    };
    return (<View style={fill ? { flex: 1 } : undefined} onLayout={($e) => {
            viewLayout.current = $e.nativeEvent.layout;
        }} {...panResponder.current.panHandlers}>
      {children}
    </View>);
};
export default DNAGestureDetector;
//# sourceMappingURL=DNAGestureDetector.js.map